import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import MediaCard from "@/components/feature/MediaCard";
import EmptyState from "@/components/feature/EmptyState";
import SectionHeading from "@/components/feature/SectionHeading";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useFavorites } from "@/hooks/useFavorites";
import { getMediaById, getRelatedMedia } from "@/lib/media";
import { TYPE_LABELS } from "@/types/movie";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const item = getMediaById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");
  const [playing, setPlaying] = useState(false);

  useDocumentMeta(
    item ? `${item.title} - 91在线观看视频` : "影片不存在 - 91在线观看视频",
    item ? `${item.summary.slice(0, 80)}……` : "该影片可能已下架或不存在。",
    item ? `${item.title},${item.title}在线观看,${item.genres.join(",")},91在线观看视频` : undefined,
  );

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  if (!item) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 md:px-8">
        <EmptyState
          icon="ri-error-warning-line"
          title="影片不存在或已下架"
          description="你访问的作品可能已被移除，去首页看看其他精彩内容吧。"
          action={
            <Link
              to="/"
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-700"
            >
              返回首页
            </Link>
          }
        />
      </div>
    );
  }

  const related = getRelatedMedia(item, 5);
  const favorited = isFavorite(item.id);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制到剪贴板");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-8 md:py-10">
      <nav className="mb-5 flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to={`/category/${item.type}`} className="transition-colors hover:text-primary-600">
          {TYPE_LABELS[item.type]}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{item.title}</span>
      </nav>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr]">
        <div className="mx-auto w-full max-w-[260px] lg:mx-0 lg:max-w-none">
          <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
            <img
              src={item.cover}
              alt={item.title}
              title={`${item.title} - 在线观看`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>

        <div className="min-w-0">
          <span className="inline-flex items-center gap-1.5 rounded-md bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700">
            {TYPE_LABELS[item.type]}
          </span>
          <h1 className="mt-3 text-2xl font-black text-foreground-950 md:text-4xl">{item.title}</h1>

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-700">
            <span className="flex items-center gap-1 font-semibold text-accent-700">
              <i className="ri-star-fill"></i>
              {item.rating.toFixed(1)} 分
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line text-foreground-400"></i>
              {item.year}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-map-pin-line text-foreground-400"></i>
              {item.region}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line text-foreground-400"></i>
              热度 {item.popularity}
            </span>
          </div>

          <div className="mt-5 space-y-3 text-sm leading-relaxed text-foreground-700">
            <p>
              <span className="mr-2 text-foreground-500">导演：</span>
              {item.director}
            </p>
            <p>
              <span className="mr-2 text-foreground-500">主演：</span>
              {item.actors.join(" / ")}
            </p>
            <p className="flex flex-wrap items-center gap-2">
              <span className="text-foreground-500">类型：</span>
              {item.genres.map((genre) => (
                <Link
                  key={genre}
                  to={`/search?q=${encodeURIComponent(genre)}`}
                  className="whitespace-nowrap rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
                >
                  {genre}
                </Link>
              ))}
            </p>
          </div>

          <p className="mt-5 text-sm leading-relaxed text-foreground-700">{item.summary}</p>

          <div className="mt-7 flex flex-wrap gap-3">
            <a
              href="#player"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-play-fill text-lg"></i>
              立即观看
            </a>
            <button
              type="button"
              onClick={() => {
                toggleFavorite(item.id);
                showToast(favorited ? "已取消收藏" : "已加入收藏");
              }}
              className={`flex items-center gap-2 whitespace-nowrap rounded-md border px-5 py-3 text-sm font-semibold transition-colors ${
                favorited
                  ? "border-accent-400 bg-accent-50 text-accent-800"
                  : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              <i className={favorited ? "ri-heart-3-fill text-accent-600" : "ri-heart-3-line"}></i>
              {favorited ? "已收藏" : "收藏"}
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-share-line"></i>
              分享
            </button>
          </div>
        </div>
      </div>

      <section id="player" className="mt-12 scroll-mt-20">
        <SectionHeading title="播放演示区" subtitle="演示交互 · 真实播放需接入影视播放接口" />
        <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-background-200 bg-primary-950">
          <img
            src={item.cover}
            alt={`${item.title} 播放画面`}
            className="h-full w-full object-cover object-top opacity-30"
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <button
              type="button"
              onClick={() => setPlaying(true)}
              className="relative flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-primary-950 transition-transform hover:scale-105 md:h-20 md:w-20"
            >
              <span className="absolute inset-0 animate-ripple rounded-full bg-accent-500/60"></span>
              <i className="relative ri-play-fill text-3xl md:text-4xl"></i>
            </button>
            <p className="mt-5 text-sm font-medium text-white md:text-base">
              {playing ? "正在准备播放…（演示模式）" : `点击播放《${item.title}》`}
            </p>
            <p className="mt-2 max-w-md px-4 text-xs text-background-50/70">
              本站播放功能仅作界面演示，不提供任何真实视频内容。接入版权播放接口后即可正常观看。
            </p>
          </div>
        </div>
      </section>

      <section className="mt-12">
        <SectionHeading title="相关推荐" subtitle="根据类型与题材为你精选" moreTo={`/category/${item.type}`} />
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 md:gap-5">
          {related.map((rel) => (
            <MediaCard key={rel.id} item={rel} />
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-24 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground-950 px-5 py-2.5 text-sm font-medium text-background-50 md:bottom-10">
          {toast}
        </div>
      )}
    </div>
  );
}